import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { Appointment, Customer } from '../types';
import { MagicBackground } from '../components/MagicBackground';
import { format, parseISO } from 'date-fns';

export default function Appointments() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [formData, setFormData] = useState({
    customerId: '',
    serviceName: '',
    startTime: '',
    duration: 60,
    notes: '',
  });

  useEffect(() => {
    loadAppointments();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAppointments({ date: selectedDate });
      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const customerList = await apiService.getCustomers();
      setCustomers(customerList);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleOpenDialog = (appointment?: Appointment) => {
    if (appointment) {
      setEditingAppointment(appointment);
      setFormData({
        customerId: appointment.customerId,
        serviceName: appointment.serviceName,
        startTime: format(parseISO(appointment.startTime), "yyyy-MM-dd'T'HH:mm"),
        duration: appointment.duration || 60,
        notes: appointment.notes || '',
      });
    } else {
      setEditingAppointment(null);
      const defaultStart = new Date();
      defaultStart.setHours(defaultStart.getHours() + 1);
      defaultStart.setMinutes(0);
      setFormData({
        customerId: '',
        serviceName: '',
        startTime: format(defaultStart, "yyyy-MM-dd'T'HH:mm"),
        duration: 60,
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAppointment(null);
  };

  const handleSaveAppointment = async () => {
    try {
      const startTime = new Date(formData.startTime);
      const endTime = new Date(startTime.getTime() + formData.duration * 60000);

      if (editingAppointment) {
        await apiService.updateAppointment(editingAppointment.appointmentId, {
          serviceName: formData.serviceName,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: formData.duration,
          notes: formData.notes,
        });
      } else {
        const customer = customers.find(c => c.customerId === formData.customerId);
        await apiService.createAppointment({
          customerId: formData.customerId,
          customerName: customer?.name || 'Unknown',
          serviceId: 'service-1', // TODO: Get from services list
          serviceName: formData.serviceName,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          duration: formData.duration,
          notes: formData.notes,
          status: 'scheduled',
        });
      }
      handleCloseDialog();
      loadAppointments();
    } catch (error) {
      console.error('Error saving appointment:', error);
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await apiService.cancelAppointment(appointmentId);
        loadAppointments();
      } catch (error) {
        console.error('Error canceling appointment:', error);
      }
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'confirmed':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      case 'no-show':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getCustomerName = (customerId: string): string => {
    const customer = customers.find(c => c.customerId === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Box maxWidth="xl" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Appointments
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' },
            }}
          >
            New Appointment
          </Button>
        </Box>

        <Card
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            mb: 3,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CalendarIcon sx={{ color: '#667eea' }} />
              <TextField
                type="date"
                label="Select Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Service</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Payment</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      No appointments scheduled for this date
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                appointments.map((appointment) => (
                  <TableRow key={appointment.appointmentId}>
                    <TableCell sx={{ color: 'white' }}>
                      {format(parseISO(appointment.startTime), 'h:mm a')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {appointment.customerName || getCustomerName(appointment.customerId)}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>{appointment.serviceName}</TableCell>
                    <TableCell sx={{ color: 'white' }}>{appointment.duration || 60} min</TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.status}
                        size="small"
                        color={getStatusColor(appointment.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {appointment.paymentStatus && (
                        <Chip
                          label={appointment.paymentStatus}
                          size="small"
                          sx={{
                            bgcolor: appointment.paymentStatus === 'paid' ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                            color: appointment.paymentStatus === 'paid' ? '#43e97b' : 'rgba(255,255,255,0.7)',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(appointment)}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
                        <IconButton
                          size="small"
                          onClick={() => handleCancelAppointment(appointment.appointmentId)}
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#1a1f3a',
              color: 'white',
            },
          }}
        >
          <DialogTitle>
            {editingAppointment ? 'Edit Appointment' : 'New Appointment'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Customer</InputLabel>
                <Select
                  value={formData.customerId}
                  onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                  label="Customer"
                  disabled={!!editingAppointment}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.customerId} value={customer.customerId}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Service Name"
                fullWidth
                required
                value={formData.serviceName}
                onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <TextField
                label="Start Time"
                type="datetime-local"
                fullWidth
                required
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <TextField
                label="Duration (minutes)"
                type="number"
                fullWidth
                required
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 60 })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAppointment}
              variant="contained"
              disabled={!formData.customerId || !formData.serviceName || !formData.startTime}
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
              }}
            >
              {editingAppointment ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
